import type { MutableRefObject } from "react";
import { Link } from "react-router-dom";
import { useMeetingIdForActivity } from "@/hooks/use-meeting-summary";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SrIcon } from "@/components/ui/SrIcon";
import CustomerLogo from "@/components/customers/CustomerLogo";
import CustomerStatusPill from "@/components/customers/CustomerStatusPill";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { useCustomerDetail } from "@/hooks/use-customer-detail";
import { useCustomerOwners } from "@/hooks/use-customer-owners";
import { useCustomerStatus } from "@/hooks/use-customer-status";
import { useLastContact } from "@/hooks/use-customer-activities";
import { useCustomerOpportunities } from "@/hooks/use-opportunities";
import { useFollowUpRuleThresholds } from "@/hooks/use-crm-config";
import { useUpdateFollowUp } from "@/hooks/use-follow-up-mutations";
import { useUsers } from "@/hooks/use-users";
import type { FollowUpWithRelations } from "@/hooks/use-follow-ups";
import type { FollowUpAction } from "@/lib/follow-up-calc";
import { activityTypeLabel } from "@/lib/customer-activity-calc";
import { contactLabel } from "@/lib/contact-display";
import { formatDate, formatNOK, formatRelativeTime } from "@/lib/format";
import { meetingFollowUpLabel, PRIORITY_LABELS, priorityTone, primaryActionFor } from "@/lib/follow-up-calc";
import { FOLLOW_UP_SOURCE_LABELS } from "@/lib/follow-up-labels";
import { isFollowUpRuleKey, ruleBadge } from "@/lib/follow-up-rules";
import { STAGE_LABELS, isOpenStage } from "@/lib/opportunity-calc";
import { osloDateString } from "@/lib/oslo-time";
import { formatPhoneDisplay } from "@/lib/phone-format";
import { cn } from "@/lib/utils";
import { TONE_PILL, TONE_TEXT } from "@/lib/widget-status";
import type { FollowUpPriority } from "@/lib/db-types";
import Kbd from "./QueueKbd";
import SwipeCard from "./SwipeCard";

interface FollowUpCardProps {
  followUp: FollowUpWithRelations;
  onAction: (action: FollowUpAction) => void;
  /** Plass blant ukens åpne oppfølginger (1-basert) og antallet. */
  /** Lar køen starte Avvis-sveipet fra tastaturet (4 og pil venstre). */
  dismissRef?: MutableRefObject<(() => void) | null>;
  position?: number;
  total?: number;
}

/**
 * Ett oppfølgingskort i køen, med samme form som signalkortet (VIB-2986): kunden
 * med logo, oppfølgingen i en grå boks, faktaraden med telefonnummeret og fire
 * store knapper. Ring er hovedknappen når oppfølgingen er en samtale. Avvis
 * sveiper kortet ut som signalkortet og lukker uten grunn.
 */
export default function FollowUpCard({ followUp, onAction, dismissRef, position, total }: FollowUpCardProps) {
  const { activeUser } = useImpersonation();
  const { data: status } = useCustomerStatus(followUp.customer_id);
  const { data: lastContact } = useLastContact(followUp.customer_id);
  const { data: opportunities = [] } = useCustomerOpportunities(followUp.customer_id);
  const { data: owners = [] } = useCustomerOwners(followUp.customer_id);
  const { data: detail } = useCustomerDetail(followUp.customer_id);
  const { data: thresholds } = useFollowUpRuleThresholds();
  const { data: users = [] } = useUsers();
  const updateFollowUp = useUpdateFollowUp();
  const meetingLabel = meetingFollowUpLabel(followUp);
  const summaryApproval = meetingLabel !== null;
  const { data: summaryMeetingId } = useMeetingIdForActivity(summaryApproval ? followUp.source_activity_id : null);

  const customerName = followUp.customer?.name ?? "Ukjent kunde";
  const today = osloDateString(new Date());
  const overdue = followUp.due_date < today;
  const tone = priorityTone(followUp.priority);
  const phoneFirst = primaryActionFor(followUp) === "phone";
  const lastBy = users.find((u) => u.id === lastContact?.last_contact_user_id)?.full_name;
  const openProcesses = opportunities.filter((o) => isOpenStage(o.stage));
  const isOwner = !!activeUser && owners.some((o) => o.user_id === activeUser.id);
  // Samme som «Tidligere verdi» på signalkortet: kontraktssum på kundens eksterne prosjekter.
  const previousValue = (detail?.projects ?? []).filter((p) => !p.is_internal).reduce((sum, p) => sum + (Number(p.total_amount) || 0), 0);
  const contact = followUp.contact
    ? contactLabel({ name: followUp.contact.full_name, title: followUp.contact.title, email: followUp.contact.email, phone: followUp.contact.phone })
    : null;
  const rawPhone = followUp.contact?.phone ?? followUp.customer?.contact_phone ?? null;
  const phone = rawPhone ? formatPhoneDisplay(rawPhone) : null;
  const sourceLabel =
    followUp.source === "rule" && isFollowUpRuleKey(followUp.rule_key)
      ? ruleBadge(followUp.rule_key, thresholds)
      : followUp.source === "rule"
        ? "Regel"
        : FOLLOW_UP_SOURCE_LABELS[followUp.source];

  const ringButton = (
    <Button key="phone" variant={phoneFirst ? "default" : "outline"} className="h-12 text-[15px]" onClick={() => onAction("phone")} aria-keyshortcuts="2">
      <SrIcon name="phone" className="mr-2 h-4 w-4" />Ring<Kbd>2</Kbd>
    </Button>
  );
  const emailButton = (
    <Button key="email" variant={phoneFirst ? "outline" : "default"} className="h-12 text-[15px]" onClick={() => onAction("email")} aria-keyshortcuts="1">
      <SrIcon name="mail" className="mr-2 h-4 w-4" />E-post<Kbd>1</Kbd>
    </Button>
  );

  return (
    <SwipeCard label={`Oppfølging: ${followUp.title}`} onDismiss={() => onAction("close")} dismissRef={dismissRef}>
      {(swipe) => (
        <>
      <div className="flex flex-wrap items-center gap-1.5">
        {status && <CustomerStatusPill status={status.effective_status} />}
        {isOwner && <span className="rounded-pill bg-secondary px-3 py-0.5 text-[13px] font-medium">Din kunde</span>}
        <Select value={followUp.priority} onValueChange={(v) => updateFollowUp.mutate({ followUp, priority: v as FollowUpPriority })}>
          <SelectTrigger
            aria-label="Endre prioritet"
            className={cn("h-7 w-auto gap-1 rounded-pill border-0 px-3 text-[13px] font-medium", tone ? TONE_PILL[tone] : "bg-muted text-muted-foreground")}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(Object.keys(PRIORITY_LABELS) as FollowUpPriority[]).map((p) => (
              <SelectItem key={p} value={p}>
                {PRIORITY_LABELS[p]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-xs text-muted-foreground">{sourceLabel}</span>
        {position && total ? (
          <span className="ml-auto text-sm tabular-nums text-muted-foreground">
            {position} / {total}
          </span>
        ) : null}
      </div>

      <div className="flex items-center gap-4">
        <div className="min-w-0 flex-1">
          <h3 className="font-display text-3xl font-semibold leading-tight tracking-tight">
            <Link to={`/kunde/${followUp.customer_id}`} className="underline-offset-4 hover:underline">
              {customerName}
            </Link>
          </h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Oppfølging · forfaller <span className={cn(overdue && TONE_TEXT.miss)}>{formatDate(followUp.due_date)}{overdue ? " (forfalt)" : ""}</span>
            {followUp.postponed_count > 0 ? ` · utsatt ${followUp.postponed_count} ${followUp.postponed_count === 1 ? "gang" : "ganger"}` : ""}
          </p>
        </div>
        <CustomerLogo name={customerName} logoUrl={detail?.customer?.logo_url ?? null} className="h-16 w-36 border-0 bg-transparent p-0 [&_img]:ml-auto" />
      </div>

      <section className="space-y-2 bg-muted px-5 py-4">
        <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">Oppfølging</p>
        <p className="font-display text-xl font-medium leading-snug">{followUp.title}</p>
        {followUp.description && <p className="whitespace-pre-line text-sm">{followUp.description}</p>}
      </section>

      <dl className="grid gap-4 border-t border-border pt-3.5 text-sm sm:grid-cols-3">
        <div>
          <dt className="text-xs text-muted-foreground">Kontakt</dt>
          <dd className="font-medium">
            {contact ? contact.primary : phone ?? "Ingen registrert"}
            {(contact?.secondary || (contact && phone)) && (
              <span className="block text-xs font-normal text-muted-foreground">
                {[contact?.secondary, contact && phone && contact.primary !== phone ? phone : null].filter(Boolean).join(" · ")}
              </span>
            )}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Tidligere verdi</dt>
          <dd className="font-medium">{detail ? formatNOK(previousValue) : "…"}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Sist kontakt</dt>
          <dd className="font-medium">
            {lastContact ? formatRelativeTime(lastContact.last_contact_at) : "Ingen registrert"}
            {lastContact && (
              <span className="block text-xs font-normal text-muted-foreground">
                {activityTypeLabel(lastContact.last_contact_type)} {formatDate(lastContact.last_contact_at)}
                {lastBy ? `, ${lastBy}` : ""}
              </span>
            )}
          </dd>
        </div>
      </dl>

      {openProcesses.length > 0 && (
        <div className="space-y-0.5 bg-muted px-3.5 py-2.5 text-sm">
          {openProcesses.map((o) => (
            <p key={o.id}>
              {(o.owner?.full_name ?? "Ukjent").split(" ")[0]} har <b>{o.title}</b> i {STAGE_LABELS[o.stage]}.
            </p>
          ))}
        </div>
      )}

      <div className="grid grid-cols-2 gap-2.5">
        {summaryApproval ? (
          summaryMeetingId && (
            <Button asChild className="col-span-2 h-12 text-[15px]">
              <Link to={`/kunde/${followUp.customer_id}/moter/${summaryMeetingId}`}>{meetingLabel}</Link>
            </Button>
          )
        ) : phoneFirst ? (
          [ringButton, emailButton]
        ) : (
          [emailButton, ringButton]
        )}
        <Button variant="secondary" className="h-12 text-[15px]" onClick={() => onAction("postpone")} aria-keyshortcuts="3">
          <SrIcon name="calendar" className="mr-2 h-4 w-4" />Utsett<Kbd>3</Kbd>
        </Button>
        <Button
          variant="outline"
          className="h-12 border-border text-[15px] text-muted-foreground hover:border-destructive hover:text-destructive"
          onClick={swipe.dismiss}
          disabled={swipe.leaving}
          aria-keyshortcuts="4"
        >
          Avvis<Kbd>4</Kbd>
        </Button>
      </div>

        </>
      )}
    </SwipeCard>
  );
}
